import { EMPTY_LIST, EMPTY_STRING } from '../../../../constants/common'
import { EMAIL_REPLY_FORM_HTML_BLOCK_ID } from '../../../../constants/email'
import { NOTIFICATION_FILE_UPLOAD_LIMIT } from '../../../../constants/notification'
import { toHTMLElement } from '../../../../utils/html'
import { addSignature, removeSignature } from '../../../../utils/signature'
import { isAIActionInsertBelow } from '../../../AITextGenerationField'
import {
  EMAIL_CREATION_FORM_RECIPIENTS_FIELD_LIMIT_NOTIFICATION,
  EMAIL_CREATION_FORM_RECIPIENTS_TOTAL_LIMIT_NOTIFICATION,
  EMAIL_CREATION_FORM_TEMPLATE_FOLDER_MASS,
  EMAIL_CREATION_FORM_TEMPLATE_FOLDERS,
} from '../EmailCreationForm.constants'

function getFileId(file) {
  const id = Number(file?.id)

  return Number.isFinite(id) ? id : null
}

function getFileSize(file) {
  return Number(file?.size) || 0
}

function getFilesWithValidIds(files = EMPTY_LIST) {
  return files.filter(file => getFileId(file) !== null)
}

function getFileIdsSet(files = EMPTY_LIST) {
  return new Set(files.map(file => getFileId(file)).filter(id => id !== null))
}

function getValidLinkIds({ files = EMPTY_LIST, filesAsLinks = EMPTY_LIST }) {
  const currentIds = new Set(
    files.map(file => getFileId(file)).filter(id => id !== null)
  )

  return filesAsLinks
    .map(id => Number(id))
    .filter(id => Number.isFinite(id) && currentIds.has(id))
}

function getForcedLinkIds({ files = EMPTY_LIST, maxSizeBytes }) {
  const linkIds = []

  for (const file of files) {
    if (getFileSize(file) > maxSizeBytes) {
      const id = getFileId(file)

      if (id !== null) {
        linkIds.push(id)
      }
    }
  }

  return linkIds
}

function getUnlinkedFiles({ files = EMPTY_LIST, linkIds }) {
  return files.filter(file => !linkIds.has(getFileId(file)))
}

function getFilesTotalSize(files = EMPTY_LIST) {
  return files.reduce((sum, file) => sum + getFileSize(file), 0)
}

function moveFilesToLinksUntilLimit({
  attachedSize,
  files = EMPTY_LIST,
  linkIds,
  maxSizeBytes,
}) {
  let nextAttachedSize = attachedSize

  for (const file of files) {
    if (nextAttachedSize <= maxSizeBytes) {
      break
    }

    const id = getFileId(file)

    if (id !== null) {
      linkIds.add(id)
      nextAttachedSize -= getFileSize(file)
    }
  }

  return nextAttachedSize
}

function getCurrentLinkFiles({
  files = EMPTY_LIST,
  selectedLinkIds = EMPTY_LIST,
}) {
  return files.filter(file => selectedLinkIds.includes(getFileId(file)))
}

function getNewFiles({ files = EMPTY_LIST, linkIds, prevFileIds }) {
  return files.filter(file => {
    const id = getFileId(file)

    return !linkIds.has(id) && !prevFileIds.has(id)
  })
}

function getRemainingFilesBySizeDesc({ files = EMPTY_LIST, linkIds }) {
  return getUnlinkedFiles({ files, linkIds }).sort(
    (a, b) => getFileSize(b) - getFileSize(a)
  )
}

function getLinkIdsInFilesOrder({ files = EMPTY_LIST, linkIds }) {
  return files
    .map(file => getFileId(file))
    .filter(id => id !== null && linkIds.has(id))
}

function extractReplyBlock(html) {
  const el = toHTMLElement(html)
  const replyNode = el.querySelector(`#${EMAIL_REPLY_FORM_HTML_BLOCK_ID}`)

  if (!replyNode) {
    return { contentHtml: html, replyBlockHtml: EMPTY_STRING }
  }

  const replyBlockHtml = replyNode.outerHTML

  replyNode.remove()

  return { contentHtml: el.innerHTML, replyBlockHtml }
}

export function getAIApplyNextValue({
  action,
  currentValue,
  generatedText,
  ignoreReplyBlock,
  insertSignatureBeforeQuotedText,
  signatureHtml,
}) {
  const safeSignatureHtml = signatureHtml || EMPTY_STRING
  const safeCurrentValue = currentValue || EMPTY_STRING

  if (ignoreReplyBlock) {
    const { contentHtml, replyBlockHtml } = extractReplyBlock(safeCurrentValue)

    let messageWithReplyBlock = replyBlockHtml
      ? `${generatedText}${replyBlockHtml}`
      : generatedText

    if (isAIActionInsertBelow(action)) {
      const userContent = safeSignatureHtml
        ? removeSignature(contentHtml)
        : contentHtml
      const combined = `${userContent}${generatedText}`

      messageWithReplyBlock = replyBlockHtml
        ? `${combined}${replyBlockHtml}`
        : combined
    }

    return safeSignatureHtml
      ? addSignature({
          insertSignatureBeforeQuotedText,
          message: messageWithReplyBlock,
          signatureHTML: safeSignatureHtml,
        })
      : messageWithReplyBlock
  }

  let message = generatedText

  if (isAIActionInsertBelow(action)) {
    const contentWithoutSignature = safeSignatureHtml
      ? removeSignature(safeCurrentValue)
      : safeCurrentValue

    message = `${contentWithoutSignature}${generatedText}`
  }

  return safeSignatureHtml
    ? addSignature({
        message,
        signatureHTML: safeSignatureHtml,
      })
    : message
}

export function getTemplateFolder({ mass, singleTemplateFolder }) {
  return EMAIL_CREATION_FORM_TEMPLATE_FOLDERS[
    mass ? EMAIL_CREATION_FORM_TEMPLATE_FOLDER_MASS : singleTemplateFolder
  ]
}

export function canSubmit({ filesError, filesUploading, notifications }) {
  return (
    !notifications.find(item =>
      [
        NOTIFICATION_FILE_UPLOAD_LIMIT,
        EMAIL_CREATION_FORM_RECIPIENTS_TOTAL_LIMIT_NOTIFICATION,
        EMAIL_CREATION_FORM_RECIPIENTS_FIELD_LIMIT_NOTIFICATION,
      ].includes(item.id)
    ) &&
    filesUploading.length === 0 &&
    filesError.length === 0
  )
}

export function splitFilesToLinksAndAttachments({
  files = EMPTY_LIST,
  filesAsLinks = EMPTY_LIST,
}) {
  const linkIds = new Set(
    getValidLinkIds({
      files,
      filesAsLinks,
    })
  )

  const linkedFiles = []
  const attachmentFiles = []

  files.forEach(file => {
    const id = getFileId(file)

    if (id !== null && linkIds.has(id)) {
      linkedFiles.push(file)
    } else {
      attachmentFiles.push(file)
    }
  })

  return { attachmentFiles, linkedFiles }
}

export function getFilesAsLinks({
  files = EMPTY_LIST,
  filesAsLinks = EMPTY_LIST,
  maxSizeBytes,
  prevFiles = EMPTY_LIST,
}) {
  const allFiles = getFilesWithValidIds(files)
  const prevFileIds = getFileIdsSet(prevFiles)

  const currentLinkIds = getValidLinkIds({
    files: allFiles,
    filesAsLinks,
  })

  const linkIds = new Set(
    getForcedLinkIds({
      files: allFiles,
      maxSizeBytes,
    })
  )
  const attachedFiles = getUnlinkedFiles({ files: allFiles, linkIds })
  let attachedSize = getFilesTotalSize(attachedFiles)

  if (attachedSize <= maxSizeBytes) {
    return getLinkIdsInFilesOrder({ files: allFiles, linkIds })
  }

  attachedSize = moveFilesToLinksUntilLimit({
    attachedSize,
    files: getCurrentLinkFiles({
      files: attachedFiles,
      selectedLinkIds: currentLinkIds,
    }),
    linkIds,
    maxSizeBytes,
  })

  attachedSize = moveFilesToLinksUntilLimit({
    attachedSize,
    files: [
      ...getNewFiles({ files: attachedFiles, linkIds, prevFileIds }),
    ].reverse(),
    linkIds,
    maxSizeBytes,
  })

  if (attachedSize > maxSizeBytes) {
    moveFilesToLinksUntilLimit({
      attachedSize,
      files: getRemainingFilesBySizeDesc({ files: attachedFiles, linkIds }),
      linkIds,
      maxSizeBytes,
    })
  }

  return getLinkIdsInFilesOrder({ files: allFiles, linkIds })
}

// The replied message subject is prefilled, so only a different one counts as written by the user
export function hasEmailFormUserWrittenSubject({ replySubject, subject }) {
  const trimmedSubject = (subject || EMPTY_STRING).trim()

  return (
    trimmedSubject.length > 0 &&
    trimmedSubject !== (replySubject || EMPTY_STRING).trim()
  )
}
